import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import CloseIcon from "@mui/icons-material/Close";
import ListSubheader from "@mui/material/ListSubheader";
import { formatProductionUnit } from "./productionUtils";
import { PERIOD_TYPE_OPTIONS } from "./productionDialogConstants";

export default function ProductionDialogs({
  periodTypeOptions = PERIOD_TYPE_OPTIONS,
  addProductionReportOpen,
  commodities,
  productionReportDialogMode,
  productionReportDeleteTarget,
  productionReportForm,
  productionReportSaveState,
  productionReportDuplicateNotice,
  resourceReserveNameById,
  productionMetrics,
  productionMetricEntries,
  productionMetricOptions,
  showAddMetricSelector,
  productionReportSourceTitleOptions = [],
  productionReportSourceUrlOptions = [],
  onCloseProductionReport,
  onCancelDeleteProductionReport,
  onConfirmDeleteProductionReport,
  onProductionReportFieldChange,
  onSelectProductionMetric,
  onProductionMetricEntryChange,
  onRemoveProductionMetric,
  onToggleAddMetricSelector,
  onSubmitProductionReport,
  onUpdateProductionReport,
}) {
  const defaultDialogContentSx = {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    mt: 1,
  };
  const excelTextFieldSx = {
    "& .MuiInputBase-input": {
      padding: "6px 8px",
      fontSize: "0.875rem", 
    },
    "& .MuiInputLabel-root": {
      fontSize: "0.875rem",
      transform: "translate(8px, 8px) scale(1)",
    },

    "& .MuiInputLabel-shrink": {
      transform: "translate(8px, -6px) scale(0.85)",
    },
  };
  const squareTextFieldSx = {
    ...excelTextFieldSx,
    "& .MuiOutlinedInput-root": {
      borderRadius: 0,
    },
  };
  const resolveReserveName = (reserveId) => {
    if (!reserveId || !resourceReserveNameById) return "";
    return (
      resourceReserveNameById[reserveId] ||
      resourceReserveNameById[`${reserveId}`] ||
      ""
    );
  };
  const reportReserveName = resolveReserveName(productionReportForm.resource_reserve_id);
  const updateProductionReportField = (field, value) => {
    onProductionReportFieldChange(field)({ target: { value } });
  };

  return (
    <>
      <Dialog
        open={addProductionReportOpen}
        onClose={onCloseProductionReport}
        fullWidth
        maxWidth="md"
        scroll="paper"
      >
        <DialogTitle>
          {productionReportDialogMode === "edit"
            ? `Edit Production Report${reportReserveName ? ` for ${reportReserveName}` : ""}`
            : `Add Production Report${reportReserveName ? ` for ${reportReserveName}` : ""}`}
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            ...defaultDialogContentSx,
            maxHeight: { xs: "90vh", md: "85vh" },
          }}
        >
          {productionReportDuplicateNotice && (
            <Alert severity="info">{productionReportDuplicateNotice}</Alert>
          )}
          {productionReportSaveState.error && (
            <Alert severity="error">{productionReportSaveState.error}</Alert>
          )}
          <TextField
            select
            label="Period type"
            value={productionReportForm.period_type}
            onChange={onProductionReportFieldChange("period_type")}
            fullWidth
            sx={excelTextFieldSx}
          >
            {periodTypeOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 2,
            }}
          >
            <TextField
              label="Period ending date"
              type="date"
              value={productionReportForm.as_of_date}
              onChange={onProductionReportFieldChange("as_of_date")}
              InputLabelProps={{ shrink: true }}
              fullWidth
              sx={excelTextFieldSx}
            />
            <TextField
              label="Fiscal year"
              value={productionReportForm.fiscal_year}
              onChange={onProductionReportFieldChange("fiscal_year")}
              type="number"
              fullWidth
              sx={excelTextFieldSx}
            />
            {productionReportForm.period_type === "quarter" && (
              <TextField
                label="Fiscal quarter"
                value={productionReportForm.fiscal_quarter}
                onChange={onProductionReportFieldChange("fiscal_quarter")}
                type="number"
                fullWidth
                sx={excelTextFieldSx}
              />
            )}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                Reporting currency:
              </Typography>
              <Chip
                size="small"
                label={productionReportForm.reporting_currency || "USD"}
                sx={{ fontWeight: 700 }}
              />
            </Box>
          </Box>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 2,
            }}
          >
            <Autocomplete
              freeSolo
              options={productionReportSourceTitleOptions}
              value={productionReportForm.source_title || ""}
              inputValue={productionReportForm.source_title || ""}
              onInputChange={(_event, value) => updateProductionReportField("source_title", value)}
              onChange={(_event, value) => updateProductionReportField("source_title", value || "")}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Source title"
                  fullWidth
                  sx={excelTextFieldSx}
                />
              )}
            />
            <Autocomplete
              freeSolo
              options={productionReportSourceUrlOptions}
              value={productionReportForm.source_url || ""}
              inputValue={productionReportForm.source_url || ""}
              onInputChange={(_event, value) => updateProductionReportField("source_url", value)}
              onChange={(_event, value) => updateProductionReportField("source_url", value || "")}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Source URL"
                  fullWidth
                  sx={excelTextFieldSx}
                />
              )}
            />
          </Box>
          <TextField
            label="Source notes"
            value={productionReportForm.source_notes}
            onChange={onProductionReportFieldChange("source_notes")}
            fullWidth
            multiline
            minRows={2}
            sx={excelTextFieldSx}
          />
          <Typography variant="subtitle2" fontWeight={700}>
            Key Production Metrics
          </Typography>
          {productionMetrics.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No production metrics available.
            </Typography>
          ) : (
            <Stack spacing={2}>
              <Box
                sx={{
                  border: "1px solid rgba(0,0,0,0.08)",
                  borderRadius: 2,
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1.6fr 1fr 1fr 0.6fr 0.2fr",
                    gap: 1,
                    px: 2,
                    py: 1,
                    backgroundColor: "rgba(0,0,0,0.03)",
                    fontWeight: 600,
                    fontSize: 13,
                  }}
                >
                  <Typography variant="caption" fontWeight={700}>
                    Metric
                  </Typography>
                  <Typography variant="caption" fontWeight={700}>
                    Commodity
                  </Typography>
                  <Typography variant="caption" fontWeight={700}>
                    Value
                  </Typography>
                  <Typography variant="caption" fontWeight={700}>
                    Unit
                  </Typography>
                  <Typography variant="caption" fontWeight={700} align="center">
                    Remove
                  </Typography>
                </Box>
                <Stack spacing={0} sx={{ px: 2, py: 1.5 }}>
                  {productionMetricEntries.map((entry, index) => {
                    const metric = productionMetricOptions.find(
                      (item) => item.id === entry.metric_id
                    );
                    const isAddRow = entry.isAddRow;
                    const canEdit = Boolean(metric);
                    return (
                      <Box
                        key={entry.row_id || `${entry.metric_id || "new"}-${index}`}
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "1.6fr 1fr 1fr 0.6fr 0.2fr",
                          gap: 1,
                          alignItems: "center",
                          py: 0.75,
                        }}
                      >
                        {isAddRow && !showAddMetricSelector ? (
                          <Button
                            size="small"
                            onClick={onToggleAddMetricSelector}
                            sx={{ justifyContent: "flex-start", px: 0 }}
                          >
                            + Add metric...
                          </Button>
                        ) : (
                          <Autocomplete
                            options={productionMetricOptions}
                            groupBy={(option) => option.category}
                            getOptionLabel={(option) => option.name || ""}
                            value={metric || null}
                            onChange={onSelectProductionMetric(entry.row_id)}
                            renderGroup={(params) => (
                              <li key={params.key}>
                                <ListSubheader>{params.group}</ListSubheader>
                                {params.children}
                              </li>
                            )}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  size="small"
                                  placeholder="Select metric"
                                  sx={excelTextFieldSx}
                                />
                              )}
                              isOptionEqualToValue={(option, value) => option.id === value.id}
                            />
                          )}
                          {canEdit ? (
                            metric?.commodity_required ? (
                              <TextField
                                select
                                size="small"
                                value={entry.commodity_id || ""}
                                onChange={onProductionMetricEntryChange(entry.row_id, "commodity_id")}
                                fullWidth
                                sx={excelTextFieldSx}
                              >
                              <MenuItem value="">Select commodity</MenuItem>
                              {commodities.map((commodity) => (
                                <MenuItem key={commodity.id} value={commodity.id}>
                                  {commodity.name}
                                </MenuItem>
                              ))}
                            </TextField>
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              Not required
                            </Typography>
                          )
                        ) : (
                          <Box />
                        )}
                        {canEdit ? (
                            <TextField
                              value={entry.value || ""}
                              onChange={onProductionMetricEntryChange(entry.row_id, "value")}
                              type="number"
                              size="small"
                              fullWidth
                              sx={squareTextFieldSx}
                            />
                          ) : (
                            <Box />
                          )}
                        <Typography variant="caption" color="text.secondary">
                          {formatProductionUnit(
                            metric?.unit,
                            productionReportForm.reporting_currency
                          ) || "-"}
                        </Typography>
                        {!isAddRow && (
                          <Button
                            size="small"
                            color="error"
                            onClick={() => onRemoveProductionMetric(entry.row_id)}
                            sx={{ minWidth: "auto", px: 0.5, justifySelf: "center" }}
                          >
                            <CloseIcon fontSize="small" />
                          </Button>
                        )}
                      </Box>
                    );
                  })}
                  {productionMetricEntries.every((entry) => entry.isAddRow) && (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                      No metrics selected yet.
                    </Typography>
                  )}
                </Stack>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onCloseProductionReport}>Cancel</Button>
          <Button
            variant="contained"
            onClick={productionReportDialogMode === "edit" ? onUpdateProductionReport : onSubmitProductionReport}
            disabled={productionReportSaveState.loading}
          >
            {productionReportSaveState.loading
              ? "Saving..."
              : productionReportDialogMode === "edit"
              ? "Update report"
              : "Save report"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(productionReportDeleteTarget)}
        onClose={onCancelDeleteProductionReport}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Delete production report?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This will delete the production report{" "}
            <strong>{productionReportDeleteTarget?.label || "for this period"}</strong>.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={onCancelDeleteProductionReport}>Cancel</Button>
          <Button color="error" variant="contained" onClick={onConfirmDeleteProductionReport}>
            Confirm delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
